export interface S3File {
  key: string;
  lastModified: string;
  size: number;
  etag: string;
}

const S3_SCRIBE_TRAINING_BUCKET_URL = 'https://ava-scribe-trainings.s3.us-west-2.amazonaws.com/';

/**
 * Fetches and parses the S3 bucket XML listing
 * @returns Array of S3 file metadata
 */
export async function fetchS3BucketListing(): Promise<S3File[]> {
  try {
    const response = await fetch(S3_SCRIBE_TRAINING_BUCKET_URL);
    if (!response.ok) {
      throw new Error(`Failed to fetch S3 bucket listing: ${response.statusText}`);
    }

    const xmlText = await response.text();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

    const contents = xmlDoc.getElementsByTagName('Contents');
    const files: S3File[] = [];

    for (let i = 0; i < contents.length; i++) {
      const content = contents[i];
      const key = content.getElementsByTagName('Key')[0]?.textContent || '';
      const lastModified = content.getElementsByTagName('LastModified')[0]?.textContent || '';
      const sizeStr = content.getElementsByTagName('Size')[0]?.textContent || '0';
      const etag = content.getElementsByTagName('ETag')[0]?.textContent || '';

      files.push({
        key,
        lastModified,
        size: parseInt(sizeStr, 10),
        etag,
      });
    }

    return files;
  } catch (error) {
    console.error('Error fetching S3 bucket listing:', error);
    throw error;
  }
}

/**
 * Constructs the full URL for an S3 file
 * @param key The file key from S3
 * @returns Full URL to the file with proper encoding (spaces as +)
 */
export function getS3FileUrl(key: string): string {
  // S3 uses + for spaces in URLs, not %20
  const encodedKey = key.replace(/ /g, '+');
  return `${S3_SCRIBE_TRAINING_BUCKET_URL}${encodedKey}`;
}

/**
 * Extracts language code from filename
 * @param filename The S3 file key
 * @returns Language code (e.g., 'en-US', 'fr-FR', 'es-ES')
 */
export function extractLanguageFromFilename(filename: string): string {
  const upperFilename = filename.toUpperCase();

  if (upperFilename.startsWith('US_') || upperFilename.startsWith('EN_')) {
    return 'en-US';
  }

  if (upperFilename.startsWith('FR_')) {
    return 'fr-FR';
  }

  if (upperFilename.startsWith('ES_')) {
    return 'es-ES';
  }

  // Default to English if unknown
  return 'en-US';
}
